import type { ThreeVisualSnapshot } from './visual-snapshot-coordinator';
import type { VisualContextPayload } from './spatial-context-types';

/**
 * Authentic camera context directly preserved from snapshot.
 * Never fabricates or substitutes fake perspective parameters.
 */
export type VisualCameraContext = VisualContextPayload['camera'];

/**
 * Item recorded in short session history ONLY after narration is actually played.
 * Strictly forbids storing base64 image data, raw pixel buffers, or sensitive keys.
 */
export interface NarratedHistoryItem {
  id: string;
  narrative: string;
  targetDetail?: string;
  modelId?: string | null;
  narratedAt: number;
}

/**
 * Metadata available about the 3D model.
 * Invariant: Only populate authentic available fields; never fabricate missing values.
 */
export interface VisualModelMetadata {
  title?: string;
  description?: string;
  author?: string;
  era?: string;
  material?: string;
  extra?: Record<string, unknown>;
}

/**
 * Options when requesting visual capture for an agent question turn.
 */
export interface VisualSourceCaptureOptions {
  turnId?: string;
  requestId?: string;
  signal?: AbortSignal;
}

/**
 * Standard reasons why a visual capture request was skipped or failed.
 * Guaranteed to provide clear status to the conversation pipeline
 * without discarding the user's active question.
 */
export type VisualCaptureSkippedReason =
  | 'disabled'             // enable_visual_capture is false or unconfigured
  | 'viewer-inactive'      // Model viewer is closed, inactive, or unmounted
  | 'model-not-loaded'     // 3D model scene is not yet loaded
  | 'tab-hidden'           // Browser tab is hidden/minimized
  | 'transport-disconnected' // Multimodal transport socket disconnected
  | 'rate-limited'         // Throttled by capture rate limit (<= 1 frame / sec)
  | 'capture-failed'       // WebGL/DOM canvas capture error
  | 'aborted'              // Turn aborted / cancelled by caller
  | 'stale-model';         // Model changed before capture could complete

/**
 * Result returned to the pipeline when capture is requested for a question turn.
 */
export interface VisualSourceCaptureResult {
  success: boolean;
  snapshot?: ThreeVisualSnapshot;
  request?: VisualAnalysisRequest;
  skippedReason?: VisualCaptureSkippedReason;
  turnId?: string;
  requestId?: string;
}

/**
 * Common visual source interface for multi-viewport coordination.
 * When 3D Model Viewer is active, pipeline uses 3D model snapshots.
 * When closed, pipeline reverts to 360 krpano view.
 */
export interface ActiveVisualSource {
  sourceId: 'krpano' | 'three-model-viewer';
  captureForQuestion: (options?: VisualSourceCaptureOptions) => Promise<VisualSourceCaptureResult>;
  isActive: () => boolean;
  modelId?: string | null;
}

/**
 * Registry coordinating the active visual image source for question turns.
 * Ensures exactly one source is captured per question without global competition.
 */
export class ActiveVisualSourceRegistry {
  private primarySources: ActiveVisualSource[] = [];
  private fallbackSource: ActiveVisualSource | null = null;

  public registerSource(source: ActiveVisualSource): () => void {
    if (source.sourceId === 'three-model-viewer') {
      this.primarySources.push(source);
      return () => {
        const index = this.primarySources.lastIndexOf(source);
        if (index !== -1) {
          this.primarySources.splice(index, 1);
        }
      };
    } else {
      this.fallbackSource = source;
      return () => {
        if (this.fallbackSource === source) {
          this.fallbackSource = null;
        }
      };
    }
  }

  public getActiveSource(): ActiveVisualSource | null {
    // 1. Check model viewers in LIFO order (most recently mounted takes precedence)
    for (let i = this.primarySources.length - 1; i >= 0; i--) {
      const source = this.primarySources[i];
      if (source.isActive()) {
        return source;
      }
    }

    // 2. CRITICAL: If any model viewer is mounted/registered (even if not active yet, e.g. loading or disabled),
    // NEVER fall back to krpano (360)!
    if (this.primarySources.length > 0) {
      return null;
    }

    // 3. Fallback to krpano (Phase 1 360 view) ONLY when NO model viewer is registered
    if (this.fallbackSource && this.fallbackSource.isActive()) {
      return this.fallbackSource;
    }
    return null;
  }

  public getActiveModelId(): string | null {
    const active = this.getActiveSource();
    if (active && active.sourceId === 'three-model-viewer') {
      return active.modelId ?? null;
    }
    return null;
  }

  public hasActiveModelViewer(): boolean {
    return this.primarySources.some((s) => s.isActive());
  }

  public hasModelViewerMounted(): boolean {
    return this.primarySources.length > 0;
  }

  public async captureForQuestion(options?: VisualSourceCaptureOptions): Promise<VisualSourceCaptureResult> {
    if (this.primarySources.length > 0) {
      for (let i = this.primarySources.length - 1; i >= 0; i--) {
        const source = this.primarySources[i];
        if (source.isActive()) {
          return source.captureForQuestion(options);
        }
      }
      return {
        success: false,
        skippedReason: 'model-not-loaded',
        turnId: options?.turnId,
        requestId: options?.requestId,
      };
    }

    if (this.fallbackSource && this.fallbackSource.isActive()) {
      return this.fallbackSource.captureForQuestion(options);
    }

    return {
      success: false,
      skippedReason: 'viewer-inactive',
      turnId: options?.turnId,
      requestId: options?.requestId,
    };
  }

  public reset(): void {
    this.primarySources = [];
    this.fallbackSource = null;
  }
}

export const activeVisualSourceRegistry = new ActiveVisualSourceRegistry();

if (typeof window !== 'undefined') {
  (window as any).__activeVisualSourceRegistry = activeVisualSourceRegistry;
}

/**
 * Payload describing an active question turn initiated by the user.
 */
export interface QuestionTurnRequest {
  turnId: string;
  requestId?: string;
  questionText?: string;
  signal?: AbortSignal;
}

/**
 * Imperative handle exposed by ThreeModelVisualAgentBridge.
 */
export interface ThreeModelVisualAgentBridgeHandle {
  captureForQuestion: (options?: VisualSourceCaptureOptions) => Promise<VisualSourceCaptureResult>;
}

/**
 * Frontend proposal for visual analysis request.
 * Prepared when a question turn occurs while viewing a 3D model.
 *
 * NOTE: [PROPOSED CONTRACT] - Proposed from Frontend Model Viewer perspective,
 * pending alignment and confirmation with Hưng's shared AI pipeline.
 */
export interface VisualAnalysisRequest {
  schemaVersion: '1.0';
  requestId: string;
  turnId?: string;
  modelId: string | null;
  modelMetadata?: VisualModelMetadata;
  image: {
    data: string; // JPEG base64 (never logged or saved to history)
    mimeType: 'image/jpeg';
    width: number;
    height: number;
    encodedBytes: number;
  };
  camera: VisualCameraContext;
  capturedAt: number;
  spatialContext?: {
    focusedObjectId?: string | null;
    visibleObjectIds?: string[];
    canQueryRag?: boolean;
    ragCollectionIds?: string[];
  };
  sessionHistory?: NarratedHistoryItem[];
}

/**
 * Possible AI decision actions.
 */
export type VisualAnalysisAction = 'narrate' | 'skip' | 'answer';

/**
 * Standard skip reasons when AI determines narration should not proceed.
 */
export type VisualAnalysisSkipReason =
  | 'no-new-detail'
  | 'already-narrated'
  | 'not-clear-enough'
  | 'low-confidence'
  | 'model-changed'
  | 'viewpoint-changed'
  | string;

/**
 * Result returned by visual analyzer.
 * Conforms to proposed independent contract pending Hưng's shared pipeline.
 */
export interface VisualAnalysisResult {
  schemaVersion: '1.0';
  requestId: string;
  action: VisualAnalysisAction;
  narrative?: string;
  targetDetail?: string;
  skipReason?: VisualAnalysisSkipReason;
  analyzedAt?: number;
}

/**
 * Proposal delivered to consumer when narration is ready.
 * Consumer must invoke `confirmNarrated()` to commit to session history once speech actually plays.
 */
export interface NarrationPlaybackProposal {
  requestId: string;
  modelId: string | null;
  narrative: string;
  targetDetail?: string;
  confirmNarrated: () => void;
}

/**
 * Pluggable analyzer interface for visual observation.
 * Allows injecting fake/simulated analyzers in unit tests and development
 * without making network calls or instantiating real Gemini clients.
 */
export interface VisualAnalyzer {
  analyze(request: VisualAnalysisRequest): Promise<VisualAnalysisResult>;
}

/**
 * In-memory manager for short narration history during an active session.
 * Enforces size limit, resets on model switch, and guarantees zero base64 persistence.
 */
export class SessionNarrationHistory {
  private items: NarratedHistoryItem[] = [];
  private maxSize: number;

  constructor(maxSize: number = 10) {
    this.maxSize = Math.max(1, maxSize);
  }

  public add(item: Omit<NarratedHistoryItem, 'narratedAt'> & { narratedAt?: number }): void {
    const cleanItem: NarratedHistoryItem = {
      id: item.id,
      narrative: item.narrative,
      targetDetail: item.targetDetail,
      modelId: item.modelId ?? null,
      narratedAt: item.narratedAt ?? Date.now(),
    };
    this.items.push(cleanItem);
    if (this.items.length > this.maxSize) {
      this.items.shift();
    }
  }

  public getHistory(): NarratedHistoryItem[] {
    return [...this.items];
  }

  public clear(): void {
    this.items = [];
  }

  public get count(): number {
    return this.items.length;
  }
}
